import { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import {
	ExpertGroupCompositionCreateRequest,
	ExpertGroupMemberCreateRequest,
	ExpertGroupMemberCreateRequestLiveServiceTypeEnum,
	ExpertGroupMemberCreateRequestTypeEnum,
	ExpertGroupRegroupRequest,
	ExpertGroupUpdateRequest
} from '@dopingtech/live_expert_bff_api_ts';
import { useApi } from '@/api/useApi';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys';
import { BOUTIQUE_GROUP_TYPE_ID, BOUTIQUE_STUDENTS_QUERY_SCOPE } from '../boutique-classroom.constants';
import { mapExpertGroup } from '../boutique-classroom.mappers';
import { ClassroomGroup } from '../boutique-classroom.types';

const EMPTY_GROUPS: ClassroomGroup[] = [];

interface CreateGroupFromStudentsParams {
	name: string;
	gradeId: number;
	studentIds: number[];
	description?: string;
}

export function useExpertGroups() {
	const { ExpertControllerApi } = useApi();
	const queryClient = useQueryClient();

	const { data, isLoading, isFetching, refetch } = useQueryProvider([QUERY_KEYS.getLoggedInExpertGroups], () => ExpertControllerApi.getLoggedInExpertGroups(), {
		select: ({ data: response }) => response?.data ?? [],
		refetchOnWindowFocus: false
	});

	const rawGroups = useMemo(() => data ?? [], [data]);
	const groups = useMemo<ClassroomGroup[]>(() => rawGroups.map(mapExpertGroup).filter((group): group is ClassroomGroup => group !== null), [rawGroups]);
	const groupIds = useMemo(() => groups.map(group => group.id), [groups]);

	const findGroupMemberId = useCallback(
		(studentId: number): number | undefined => {
			for (const group of rawGroups) {
				const member = group.members?.find(item => item.memberId === studentId && !item.deactivatedAt);
				if (member?.id !== undefined) return member.id;
			}
			return undefined;
		},
		[rawGroups]
	);

	const invalidate = useCallback(
		() =>
			Promise.all([
				queryClient.invalidateQueries([QUERY_KEYS.getLoggedInExpertGroups]),
				queryClient.invalidateQueries([QUERY_KEYS.findStudentDetailedStatisticsOfLoggedInExpert, BOUTIQUE_STUDENTS_QUERY_SCOPE])
			]),
		[queryClient]
	);

	const createComposition = useMutationProvider((request: ExpertGroupCompositionCreateRequest) => ExpertControllerApi.createGroupComposition(request), { onSuccess: invalidate });
	const updateGroup = useMutationProvider(({ groupId, request }: { groupId: number; request: ExpertGroupUpdateRequest }) => ExpertControllerApi.updateGroup(groupId, request), {
		onSuccess: invalidate
	});
	const regroup = useMutationProvider(({ groupId, request }: { groupId: number; request: ExpertGroupRegroupRequest }) => ExpertControllerApi.regroupGroup(groupId, request), {
		onSuccess: invalidate
	});
	const removeMember = useMutationProvider((groupMemberId: number) => ExpertControllerApi.removeGroupMember(groupMemberId), { onSuccess: invalidate });
	const addMember = useMutationProvider(({ groupId, request }: { groupId: number; request: ExpertGroupMemberCreateRequest }) => ExpertControllerApi.addGroupMember(groupId, request), {
		onSuccess: invalidate
	});

	const createGroupFromStudents = useCallback(
		({ name, gradeId, studentIds, description = '' }: CreateGroupFromStudentsParams) => createComposition.safeMutate({ typeId: BOUTIQUE_GROUP_TYPE_ID, gradeId, name, description, studentIds }),
		[createComposition]
	);

	const renameGroup = useCallback((groupId: number, name: string) => updateGroup.safeMutate({ groupId, request: { name } }), [updateGroup]);

	const moveStudentsToGroup = useCallback((groupId: number, studentIds: number[]) => regroup.safeMutate({ groupId, request: { studentIds } }), [regroup]);

	const removeStudentFromGroup = useCallback(
		(studentId: number) => {
			const groupMemberId = findGroupMemberId(studentId);
			return groupMemberId === undefined ? Promise.resolve(undefined) : removeMember.safeMutate(groupMemberId);
		},
		[findGroupMemberId, removeMember]
	);

	const removeGroupMember = useCallback((groupMemberId: number) => removeMember.safeMutate(groupMemberId), [removeMember]);

	const addExpertToGroup = useCallback(
		(groupId: number, expertId: number, branchId?: number) =>
			addMember.mutateAsync({
				groupId,
				request: {
					memberId: expertId,
					type: ExpertGroupMemberCreateRequestTypeEnum.Expert,
					liveServiceType: ExpertGroupMemberCreateRequestLiveServiceTypeEnum.PlannedLecture,
					branchId
				}
			}),
		[addMember]
	);

	const isMutating = createComposition.isLoading || updateGroup.isLoading || regroup.isLoading || removeMember.isLoading || addMember.isLoading;

	return {
		groups: groups.length ? groups : EMPTY_GROUPS,
		groupIds,
		isLoading,
		isFetching,
		isMutating,
		refetch,
		findGroupMemberId,
		createGroupFromStudents,
		renameGroup,
		moveStudentsToGroup,
		removeStudentFromGroup,
		removeGroupMember,
		addExpertToGroup
	};
}
